import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface CreateGeneralExpenseRequest {
  paymentAccountSource: 'REFERENCE_FILE' | 'OTHER_BANK_ACCOUNT';
  bankName?: string;
  accountNumber?: string;
  expenseTypeId: string;
  description: string;
  actualTransferAmount: number;
  submitForReview?: true;
}

export interface GeneralExpenseItem {
  id: string;
  expenseNo: string;
  expenseTypeId: string;
  expenseNameSnapshot: string;
  description: string;
  actualTransferAmount: number;
  paymentAccountSource: 'REFERENCE_FILE' | 'OTHER_BANK_ACCOUNT' | null;
  bankName: string | null;
  accountNumber: string | null;
  attachmentPath: string | null;
  receiptUrl: string | null;
  receiptPath: string | null;
  status: string;
  requestedByName: string;
}

@Injectable({ providedIn: 'root' })
export class GeneralExpenseService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/expenses/general`;
  private readonly assetBaseUrl = environment.apiUrl.replace(/\/api\/?$/, '');

  fileUrl(path: string | null): string | null {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    return `${this.assetBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  async getAll(): Promise<GeneralExpenseItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ expenses: GeneralExpenseItem[] }>(this.apiUrl, { withCredentials: true }),
    );
    return response.expenses;
  }

  async getExpenseTypes(): Promise<Array<{ id: string; name: string; targetType: 'GENERAL' }>> {
    const response = await firstValueFrom(
      this.http.get<{
        expenseTypes: Array<{ id: string; name: string; targetType: 'GENERAL' }>;
      }>(
        `${this.apiUrl}/expense-types`,
        { withCredentials: true },
      ),
    );
    return response.expenseTypes.filter((expenseType) => expenseType.targetType === 'GENERAL');
  }

  async create(input: CreateGeneralExpenseRequest, attachment: File | null): Promise<GeneralExpenseItem> {
    const response = await firstValueFrom(
      this.http.post<{ expense: GeneralExpenseItem }>(
        this.apiUrl,
        this.toFormData(input, attachment),
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  async update(
    id: string,
    input: CreateGeneralExpenseRequest,
    attachment: File | null,
  ): Promise<GeneralExpenseItem> {
    const response = await firstValueFrom(
      this.http.put<{ expense: GeneralExpenseItem }>(
        `${this.apiUrl}/${id}`,
        this.toFormData(input, attachment),
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  async delete(id: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${id}`, { withCredentials: true }),
    );
  }

  async submitReceipt(id: string, receipt: File): Promise<void> {
    const formData = new FormData();
    formData.append('expenseReceipt', receipt);
    await firstValueFrom(
      this.http.post(`${this.apiUrl}/${id}/receipt`, formData, { withCredentials: true }),
    );
  }

  private toFormData(input: CreateGeneralExpenseRequest, attachment: File | null): FormData {
    const formData = new FormData();
    formData.append('paymentAccountSource', input.paymentAccountSource);
    if (input.bankName) formData.append('bankName', input.bankName);
    if (input.accountNumber) formData.append('accountNumber', input.accountNumber);
    formData.append('expenseTypeId', input.expenseTypeId);
    formData.append('description', input.description);
    formData.append('actualTransferAmount', String(input.actualTransferAmount));
    if (input.submitForReview) formData.append('submitForReview', 'true');
    if (attachment) formData.append('expenseAttachment', attachment);
    return formData;
  }
}
